import { Box, Skeleton, Stack, Typography } from '@mui/material';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import useDashboardQueryFetch from '../../../../../hooks/useDashboardQueryFetch';
import theme from '../../../../../theme';
import { Dashboard } from '../../../../../types/files';
import KeyPeopleModal from '../../../../modals/tables/dashboards/rows/KeyPeopleModal';
import useStyles from "../styles";

const KeyPeopleCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { query: keyPeople, queryLoaded: keyPeopleLoaded } = useDashboardQueryFetch<string>(dashboard.id, 'Key People');

    const [keyPeopleModalOpen, setKeyPeopleModalOpen] = useState<boolean>(false);
    const keyPeopleRef = useRef<HTMLElement>(null);

    const getRoleRank = useCallback((role: string): number => {
        const roleUpper = role?.toUpperCase() || '';

        // Rank 1 – Founder (ownership) / CEO (highest executive)
        if (roleUpper.includes('FOUNDER') || roleUpper.includes('CEO')) return 1;
        // Rank 2 – President (if separate from CEO)
        if (roleUpper.includes('PRESIDENT') && !roleUpper.includes('VICE')) return 2;
        // Rank 3 – All C-suite (COO, CFO, CTO, CMO, CHRO, etc.)
        if (roleUpper.includes('CHIEF') ||
            (roleUpper.includes('OFFICER') && (roleUpper.includes('CFO') || roleUpper.includes('COO') || roleUpper.includes('CTO') || roleUpper.includes('CMO') || roleUpper.includes('CPO') || roleUpper.includes('CRO') || roleUpper.includes('CHRO') || roleUpper.includes('CDO') || roleUpper.includes('CIO') || roleUpper.includes('CSO') || roleUpper.includes('CCO'))) ||
            /^C[A-Z]O\b/.test(roleUpper)) return 3;
        // Rank 4 – Executive VP / Senior VP
        if ((roleUpper.includes('EXECUTIVE') && roleUpper.includes('VP')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VP')) ||
            (roleUpper.includes('EXECUTIVE') && roleUpper.includes('VICE PRESIDENT')) ||
            (roleUpper.includes('SENIOR') && roleUpper.includes('VICE PRESIDENT'))) return 4;
        // Rank 5 – VP
        if (roleUpper.includes('VICE PRESIDENT') || roleUpper.includes('VP')) return 5;
        // Rank 6 – Head of / Director
        if (roleUpper.includes('HEAD OF') || roleUpper.includes('DIRECTOR')) return 6;
        // Rank 7 – Senior Managers / Senior Specialists
        if (roleUpper.includes('SENIOR')) return 7;
        // Rank 8 – Mid-level & Junior roles
        if (roleUpper.includes('MANAGER') || roleUpper.includes('LEAD') || roleUpper.includes('SPECIALIST') ||
            roleUpper.includes('ANALYST') || roleUpper.includes('COORDINATOR') || roleUpper.includes('JUNIOR')) return 8;
        // Rank 9 – Administrative & Support staff
        if (roleUpper.includes('SECRETARY') || roleUpper.includes('ASSISTANT') || roleUpper.includes('CLERK') ||
            roleUpper.includes('ADMIN') || roleUpper.includes('SUPPORT')) return 9;

        // All others (default)
        return 10;
    }, []);

    const sortedKeyPeople: any[] = useMemo(() => {
        if (Array.isArray(keyPeople)) {
            return keyPeople.sort((a: any, b: any) => {
                const rankA = getRoleRank(a.role);
                const rankB = getRoleRank(b.role);

                if (rankA !== rankB)
                    return rankA - rankB;

                if (a.role < b.role) return -1;
                if (a.role > b.role) return 1;

                return 0;
            });
        }

        return keyPeople as unknown as any[];
    }, [getRoleRank, keyPeople]);

    const initials = useMemo(() => {
        const name = sortedKeyPeople?.[0]?.title?.split('|')?.[0]?.split('-')?.[0];
        return name ? name.split(' ').slice(0, 2).map((word: string) => word.charAt(0).toUpperCase()).join('') : ''
    }, [sortedKeyPeople]);

    return (<>
        <Stack width="100%" ref={keyPeopleRef}>
            {keyPeopleLoaded ? (Array.isArray(sortedKeyPeople) && !!sortedKeyPeople.length ? (<>
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" width="100%"
                        onClick={() => setKeyPeopleModalOpen(true)} sx={{ cursor: 'pointer' }}>
                        <Box pt="4px">
                            <Stack alignItems="center" justifyContent="center" p={2} width={32} height={32}
                                sx={{ borderRadius: 4, background: 'linear-gradient(90deg, #2dd4bf 0%, #0d9488 100%)' }}>
                                <Typography fontSize="0.75rem" fontWeight={600} fontFamily="Lato" color="#fff">
                                    {initials || ''}
                                </Typography>
                            </Stack>
                        </Box>
                        <Typography className={classes.person}
                            fontFamily="Lato !important"
                            fontWeight="400 !important"
                            fontSize="0.95rem !important"
                            color="#334155 !important"
                            sx={{ "&:hover": { textDecoration: 'underline' }, cursor: 'pointer' }}>
                            {sortedKeyPeople[0]?.title?.split('|')?.[0]?.split('-')?.[0] || '[Person]'}
                        </Typography>
                        <Typography fontFamily="Inter" fontSize="0.9rem" fontWeight={600} color="#64748b">
                            {(sortedKeyPeople.length > 1) && (
                                <span>{` + ${sortedKeyPeople.length - 1} other${sortedKeyPeople.length - 1 > 1 ? 's' : ''}`}</span>
                            )}
                        </Typography>
                    </Stack>
                </Stack>
            </>) : (
                <Typography className={classes.url} color={`${theme.colors.neutral['500']} !important`}>
                    {'No key people found'}
                </Typography>
            )) : (
                <Box sx={{ width: '100%' }}>
                    <Skeleton animation="wave" className={classes.wave} />
                </Box>
            )}
        </Stack>
        {keyPeopleModalOpen && (
            <KeyPeopleModal open={keyPeopleModalOpen}
                dashboard={dashboard}
                anchorElement={keyPeopleRef.current}
                onClose={() => setKeyPeopleModalOpen(false)} />
        )}
    </>);
}

export default memo(KeyPeopleCell);
